import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';

/**
 * Action d'app en attente : le SHELL la pose quand l'utilisateur declenche un
 * shortcut d'app depuis le dock (ex. "Nouveau contact") ; l'APP la consomme au
 * montage. Circuit type cote app, dans un useEffect : lire `actionId` via
 * usePendingAppAction, ouvrir le Modal correspondant, puis appeler `consume()`
 * pour marquer l'action traitee (le shell la remet a null).
 */
export interface PendingAppAction {
  /** Id de l'app visee (celui du registre / installedApps). */
  appId: string;
  /** Id de l'action declaree par l'app (champ `actions` du registre). */
  actionId: string;
}

/** Valeur fournie par le shell au provider. */
export interface PendingActionContextValue {
  /** L'action en attente, ou null quand il n'y a rien a traiter. */
  pending: PendingAppAction | null;
  /** Marque l'action traitee : le shell remet `pending` a null. */
  consume: () => void;
}

const PendingActionContext = createContext<PendingActionContextValue>({
  pending: null,
  consume: () => {},
});

/**
 * Fournisseur pose par le SHELL autour des ecrans d'app (contexte, pour ne pas
 * cabler chaque app). Les apps ne l'utilisent jamais directement : elles
 * passent par usePendingAppAction.
 */
export function PendingActionProvider({
  value,
  children,
}: {
  value: PendingActionContextValue;
  children: ReactNode;
}): JSX.Element {
  return (
    <PendingActionContext.Provider value={value}>{children}</PendingActionContext.Provider>
  );
}

/**
 * Cote APP : lit l'action en attente destinee a CETTE app. `actionId` n'est
 * non null que si l'action vise `appId` ; les actions des autres apps restent
 * invisibles. Appeler `consume()` une fois l'action prise en charge.
 */
export function usePendingAppAction(appId: string): {
  actionId: string | null;
  consume: () => void;
} {
  const { pending, consume } = useContext(PendingActionContext);
  const actionId = pending != null && pending.appId === appId ? pending.actionId : null;
  return { actionId, consume };
}
